import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Truck, Package, Clock, MapPin, Shield, Zap, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useLanguage } from "@/lib/i18n"

export default function ServicesSection() {
  const { t } = useLanguage()

  const services = [
    {
      icon: Truck,
      titleKey: "services.ftl.title",
      descKey: "services.ftl.desc",
      features: ["services.dedicated", "services.direct", "services.tracking"],
    },
    {
      icon: Package,
      titleKey: "services.ltl.title",
      descKey: "services.ltl.desc",
      features: ["services.efficient", "services.flexible", "services.consolidated"],
    },
    {
      icon: Clock,
      titleKey: "services.expedited.title",
      descKey: "services.expedited.desc",
      features: ["services.247", "services.priority", "services.guaranteed"],
    },
    {
      icon: MapPin,
      titleKey: "services.regional.title",
      descKey: "services.regional.desc",
      features: ["services.eastCoast", "services.onTime", "services.reliable"],
    },
    {
      icon: Shield,
      titleKey: "services.specialized.title",
      descKey: "services.specialized.desc",
      features: ["services.trained", "services.specialized.equipment", "services.safety"],
    },
    {
      icon: Zap,
      titleKey: "services.dedicated.title",
      descKey: "services.dedicated.desc",
      features: ["services.custom", "services.assigned", "services.consistent"],
    },
  ]

  return (
    <section id="services" className="py-20 bg-secondary">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">{t("services.tagline")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">{t("services.title")}</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">{t("services.description")}</p>
        </div>

        {/* Services grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <Card
              key={service.titleKey}
              className="bg-card border-border hover:border-primary/50 transition-all duration-300 group"
            >
              <CardHeader>
                <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <CardTitle className="text-xl text-card-foreground">{t(service.titleKey)}</CardTitle>
                <CardDescription className="text-muted-foreground leading-relaxed">
                  {t(service.descKey)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {service.features.map((featureKey) => (
                    <li key={featureKey} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      {t(featureKey)}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Button size="lg" asChild className="group">
            <Link to="#contact">
              {t("services.cta")}
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  )
}
